import {
    ArrowDropDown,
    DeleteOutlined,
    FileDownloadOutlined,
    ForwardToInboxOutlined,
    ReceiptLongOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';

import Loader from 'components/v2/common/loader';
import Result from 'components/v2/common/Result';

import type { MilestoneApp } from 'api';
import useTranslate from 'hooks/useTranslate';

export type MilestoneInvoiceAction = 'view' | 'download' | 'send';
export type MilestoneStatusField = 'status' | 'payment_status';
export type MilestoneEditableField = 'name' | 'description' | 'price';

export interface PendingInvoiceAction {
    milestoneId: number;
    action: MilestoneInvoiceAction;
}

export interface StatusUpdateState {
    milestoneId: number;
    field: MilestoneStatusField;
}

interface StatusOption {
    value: string;
    labelKey: string;
    color: 'warning' | 'info' | 'success' | 'error';
}

interface MilestonesTableProps {
    milestones: MilestoneApp[];
    loading: boolean;
    showInvoiceActions: boolean;
    canEditPaymentStatus: boolean;
    canEditMilestones: boolean;
    saving: boolean;
    statusUpdating: StatusUpdateState | null;
    pendingAction: PendingInvoiceAction | null;
    /**
     * Workflow status + payment status are back-office tracking. They must
     * not appear on the offer that is written into a retainer.
     */
    showStatusColumns?: boolean;
    /**
     * Workflow + payment chips. Defaults to `canEditMilestones` so the
     * milestones dialog stays unchanged; the case offer table passes `true`
     * so status is editable in view mode.
     */
    canEditStatus?: boolean;
    onInvoiceAction: (action: MilestoneInvoiceAction, milestone: MilestoneApp) => void;
    onStatusChange: (milestone: MilestoneApp, field: MilestoneStatusField, value: string) => void;
    onMilestoneFieldChange: (
        milestone: MilestoneApp,
        field: MilestoneEditableField,
        value: string
    ) => void;
    onDeleteMilestone: (milestone: MilestoneApp) => void;
}

const PAYMENT_STATUS_OPTIONS: StatusOption[] = [
    { value: 'PENDING', labelKey: 'paymentStatusPending', color: 'warning' },
    { value: 'PARTIALLY_PAID', labelKey: 'paymentStatusPartiallyPaid', color: 'info' },
    { value: 'FULLY_PAID', labelKey: 'paymentStatusFullyPaid', color: 'success' },
    { value: 'REFUNDED', labelKey: 'paymentStatusRefunded', color: 'error' },
];

const MILESTONE_STATUS_OPTIONS: StatusOption[] = [
    { value: 'PENDING', labelKey: 'milestoneStatusPending', color: 'warning' },
    { value: 'IN_PROCESS', labelKey: 'milestoneStatusInProcess', color: 'info' },
    { value: 'COMPLETED', labelKey: 'milestoneStatusCompleted', color: 'success' },
];

const STATUS_ALIASES: Record<string, string> = {
    'IN PROCESS': 'IN_PROCESS',
};

const optionForValue = (options: StatusOption[], value: string | null | undefined) => {
    const normalized = value ? STATUS_ALIASES[value] || value : '';
    return options.find((option) => option.value === normalized);
};

const formatMilestoneAmount = (price: string, currency: string): string => {
    const amount = Number(price);
    if (!price || !Number.isFinite(amount)) return '--';

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${currency} ${amount.toLocaleString()}`.trim();
    }
};

const StatusChip = ({
    currentValue,
    options,
    editable,
    loading,
    onOpen,
}: {
    currentValue: string;
    options: StatusOption[];
    editable: boolean;
    loading: boolean;
    onOpen: (anchorEl: HTMLElement) => void;
}) => {
    const { translate } = useTranslate();
    const option = optionForValue(options, currentValue);

    return (
        <Chip
            size="small"
            variant="outlined"
            color={option?.color}
            label={option ? translate(option.labelKey) : currentValue || '--'}
            disabled={loading}
            onClick={editable ? (event) => onOpen(event.currentTarget) : undefined}
            onDelete={editable ? (event) => onOpen(event.currentTarget as HTMLElement) : undefined}
            deleteIcon={
                editable ? (
                    loading ? (
                        <CircularProgress size={14} color="inherit" />
                    ) : (
                        <ArrowDropDown />
                    )
                ) : undefined
            }
            sx={{
                fontWeight: 600,
                maxWidth: 'none',
                '& .MuiChip-label': {
                    overflow: 'visible',
                    textOverflow: 'clip',
                    whiteSpace: 'nowrap',
                    px: 1,
                },
            }}
        />
    );
};

const MilestonesTable = ({
    milestones,
    loading,
    showInvoiceActions,
    canEditPaymentStatus,
    canEditMilestones,
    saving,
    statusUpdating,
    pendingAction,
    showStatusColumns = true,
    canEditStatus: canEditStatusProp,
    onInvoiceAction,
    onStatusChange,
    onMilestoneFieldChange,
    onDeleteMilestone,
}: MilestonesTableProps) => {
    const { translate } = useTranslate();
    const showActionsColumn = showInvoiceActions || canEditMilestones;
    const canEditStatus = canEditStatusProp ?? canEditMilestones;
    const [statusMenu, setStatusMenu] = useState<{
        anchorEl: HTMLElement;
        milestone: MilestoneApp;
        field: MilestoneStatusField;
        options: StatusOption[];
        currentValue: string;
    } | null>(null);

    const closeStatusMenu = () => {
        setStatusMenu(null);
    };

    const handleStatusSelect = (value: string) => {
        if (statusMenu && statusMenu.currentValue !== value) {
            onStatusChange(statusMenu.milestone, statusMenu.field, value);
        }
        closeStatusMenu();
    };

    if (loading) {
        return <Loader isCircular />;
    }

    if (!milestones.length) {
        return (
            <Result
                icon={<ReceiptLongOutlined sx={{ fontSize: 32 }} />}
                title={translate('noMilestonesYet')}
                description={translate('noMilestonesYetDescription')}
                sx={{ background: 'transparent', borderColor: 'transparent' }}
            />
        );
    }

    const renderActionButton = (
        milestone: MilestoneApp,
        action: MilestoneInvoiceAction,
        titleKey: string,
        icon: React.ReactNode
    ) => {
        const isPending =
            pendingAction?.milestoneId === milestone.id && pendingAction?.action === action;

        return (
            <Tooltip title={translate(titleKey)}>
                <span>
                    <IconButton
                        size="small"
                        aria-label={translate(titleKey)}
                        disabled={pendingAction !== null}
                        onClick={() => onInvoiceAction(action, milestone)}
                    >
                        {isPending ? <CircularProgress size={16} color="inherit" /> : icon}
                    </IconButton>
                </span>
            </Tooltip>
        );
    };

    return (
        <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                <Table size="small" aria-label={translate('caseMilestones')}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, width: '26%' }}>
                                {translate('milestone')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, width: '32%' }}>
                                {translate('description')}
                            </TableCell>
                            {/* <TableCell sx={{ fontWeight: 700 }}>{translate('invoiceId')}</TableCell> */}
                            <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                {translate('amount')}
                            </TableCell>
                            {showStatusColumns ? (
                                <>
                                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {translate('status')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        {translate('paymentStatus')}
                                    </TableCell>
                                </>
                            ) : null}
                            {showActionsColumn && (
                                <TableCell sx={{ fontWeight: 700 }} align="center">
                                    {translate('actions')}
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {milestones.map((milestone) => {
                            const isStatusUpdating =
                                showStatusColumns &&
                                statusUpdating?.milestoneId === milestone.id &&
                                statusUpdating.field === 'status';
                            const isPaymentUpdating =
                                showStatusColumns &&
                                statusUpdating?.milestoneId === milestone.id &&
                                statusUpdating.field === 'payment_status';

                            return (
                                <TableRow key={milestone.id} hover>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        {canEditMilestones ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={milestone.name}
                                                placeholder={translate('milestoneName')}
                                                disabled={saving}
                                                error={!milestone.name.trim()}
                                                slotProps={{ htmlInput: { maxLength: 64 } }}
                                                onChange={(event) =>
                                                    onMilestoneFieldChange(
                                                        milestone,
                                                        'name',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        ) : (
                                            milestone.name || '--'
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {canEditMilestones ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                multiline
                                                maxRows={3}
                                                value={milestone.description}
                                                placeholder={translate('milestoneExplanation')}
                                                disabled={saving}
                                                error={!milestone.description.trim()}
                                                slotProps={{ htmlInput: { maxLength: 128 } }}
                                                onChange={(event) =>
                                                    onMilestoneFieldChange(
                                                        milestone,
                                                        'description',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {milestone.description || '--'}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {canEditMilestones ? (
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={milestone.price}
                                                placeholder={translate('amount')}
                                                disabled={saving}
                                                error={!milestone.price.toString().trim()}
                                                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                                                sx={{ minWidth: 90 }}
                                                onChange={(event) =>
                                                    onMilestoneFieldChange(
                                                        milestone,
                                                        'price',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        ) : (
                                            formatMilestoneAmount(
                                                milestone.price,
                                                milestone.currency
                                            )
                                        )}
                                    </TableCell>
                                    {showStatusColumns ? (
                                        <>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <StatusChip
                                                    currentValue={milestone.status}
                                                    options={MILESTONE_STATUS_OPTIONS}
                                                    editable={canEditStatus}
                                                    loading={isStatusUpdating}
                                                    onOpen={(anchorEl) =>
                                                        setStatusMenu({
                                                            anchorEl,
                                                            milestone,
                                                            field: 'status',
                                                            options: MILESTONE_STATUS_OPTIONS,
                                                            currentValue:
                                                                optionForValue(
                                                                    MILESTONE_STATUS_OPTIONS,
                                                                    milestone.status
                                                                )?.value || milestone.status,
                                                        })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <StatusChip
                                                    currentValue={milestone.payment_status}
                                                    options={PAYMENT_STATUS_OPTIONS}
                                                    editable={canEditStatus && canEditPaymentStatus}
                                                    loading={isPaymentUpdating}
                                                    onOpen={(anchorEl) =>
                                                        setStatusMenu({
                                                            anchorEl,
                                                            milestone,
                                                            field: 'payment_status',
                                                            options: PAYMENT_STATUS_OPTIONS,
                                                            currentValue: milestone.payment_status,
                                                        })
                                                    }
                                                />
                                            </TableCell>
                                        </>
                                    ) : null}
                                    {showActionsColumn && (
                                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                sx={{ justifyContent: 'center' }}
                                            >
                                                {showInvoiceActions && (
                                                    <>
                                                        {renderActionButton(
                                                            milestone,
                                                            'view',
                                                            'viewInvoice',
                                                            <VisibilityOutlined fontSize="small" />
                                                        )}
                                                        {renderActionButton(
                                                            milestone,
                                                            'download',
                                                            'downloadInvoice',
                                                            <FileDownloadOutlined fontSize="small" />
                                                        )}
                                                        {renderActionButton(
                                                            milestone,
                                                            'send',
                                                            'sendInvoice',
                                                            <ForwardToInboxOutlined fontSize="small" />
                                                        )}
                                                    </>
                                                )}
                                                {canEditMilestones && (
                                                    <Tooltip title={translate('delete')}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={translate('delete')}
                                                                disabled={saving}
                                                                onClick={() =>
                                                                    onDeleteMilestone(milestone)
                                                                }
                                                            >
                                                                <DeleteOutlined fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={statusMenu?.anchorEl}
                open={statusMenu !== null}
                onClose={closeStatusMenu}
            >
                {(statusMenu?.options ?? []).map((option) => (
                    <MenuItem
                        key={option.value}
                        selected={statusMenu?.currentValue === option.value}
                        onClick={() => handleStatusSelect(option.value)}
                    >
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: `${option.color}.main`,
                                mr: 1,
                                flexShrink: 0,
                            }}
                        />
                        {translate(option.labelKey)}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default MilestonesTable;
